import type { ServerErrorFrame } from "../state/server-refusal.js";
import { ReadReceiptAcks, SentReadStamps } from "./sent-read-stamps.js";

// Lazy import: server-refusal -> state.svelte -> client is a load cycle.
function reportReadRefusal(frame: ServerErrorFrame, workspaceId: string | undefined): void {
  void import("../state/server-refusal.js").then((m) =>
    m.reportServerErrorFrame(frame, workspaceId),
  );
}

/** The pending-request entry of one read receipt (shape of the client's PendingRequest). */
export interface ReadReceiptEntry {
  resolve: () => void;
  reject: (err: Error, refusal?: Record<string, unknown>) => void;
  timer: ReturnType<typeof setTimeout>;
}

interface PendingEntries {
  delete(requestId: string): unknown;
  set(requestId: string, entry: ReadReceiptEntry): unknown;
}

// Everything a client remembers about the read receipts it sent, per scope `c:ws:channel` / `d:ws:peer`.
export class ReadReceiptTracker {
  // Last cursor put on the wire per scope: a repeat with nothing new read is dropped (it took a row
  // lock on the same dm_reads/message_reads row). Cleared on disconnect so a new login starts clean.
  private readonly lastSent = new Map<string, number>();
  // The stamps actually sent, so a read_broadcast carrying one is this client's own
  // echo (the daemon broadcasts to the sender too) — see isOwn.
  private readonly stamps = new SentReadStamps();
  private readonly acks = new ReadReceiptAcks();

  /** Drop a receipt whose cursor has not moved since the last one we sent. */
  isRedundant(key: string, lastReadAt: number | undefined): boolean {
    if (lastReadAt === undefined) return false; // server-stamped: cannot compare
    const sent = this.lastSent.get(key);
    if (sent !== undefined && lastReadAt <= sent) return true;
    this.lastSent.set(key, lastReadAt);
    this.stamps.record(key, lastReadAt);
    return false;
  }

  /** A `read_broadcast` carrying a stamp this client sent for `scope`. */
  isOwn(scope: string, lastReadAt: number): boolean {
    return this.stamps.isOwn(scope, lastReadAt);
  }

  settledAt(scope: string): number | undefined {
    return this.acks.at(scope);
  }

  clear(): void {
    this.lastSent.clear();
    this.stamps.clear();
    this.acks.clear();
  }

  /** Marks the receipt in flight and registers its entry in `pending`, dropped again when it settles. */
  track(
    pending: PendingEntries,
    scope: string,
    requestId: string,
    frame: Record<string, unknown>,
    timeoutMs: number,
  ): void {
    this.acks.sent(scope);
    let done = false;
    const settle = () => {
      if (done) return;
      done = true;
      pending.delete(requestId);
      this.acks.settled(scope);
    };
    const timer = setTimeout(settle, timeoutMs);
    // The refusal meets this entry, not client.on("error"): report it here. Drops and timeouts
    // carry no frame.
    const reject = (err: Error, refusal?: Record<string, unknown>) => {
      settle();
      if (!refusal) return;
      const report: ServerErrorFrame = {
        code: refusal.code,
        error: refusal.error,
        message: err.message,
        requestId,
        recorded: refusal.recorded === true,
      };
      reportReadRefusal(
        report,
        typeof frame.workspaceId === "string" ? frame.workspaceId : undefined,
      );
    };
    pending.set(requestId, { resolve: settle, reject, timer });
  }
}
